import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { db, auth, functions } from '../firebase';
import { collection, onSnapshot, query, doc, getDoc, setDoc, updateDoc, where, getDocs, documentId, orderBy, limit } from 'firebase/firestore';
import { httpsCallable, getFunctions } from 'firebase/functions';
import { getAuth, signInWithCustomToken } from 'firebase/auth';
import { ipfsGateway, FIREBASE_PROJECT_ID, API_BASE_URL, BASE_MAX_STAMINA, TOKEN_CONTRACT, TOKENS, tokenContracts, tokenDecimals, buildingConfigClient, ATOMIC_API_ENDPOINTS, itemTemplates, EMBLEM_NAMES, MASTER_ACCOUNT_WAX_ID, ADMIN_ACCOUNTS, STAKING_TIERS, VAULT_TIERS, VAULT_TOKEN_INFO, VAULT_TOKEN_VALUE, getUpgradeCostClient, Client, calculateDiminishedBoost, calculateMaxHp, calculateXpForNextLevel } from '../constants/gameConfig';
import characterImage from '../assets/firecat.png';
import placeholderImage from '../assets/equip.png';
function PartnerPortal({ session, initiatePartnerPoolCallable, verifyPartnerDepositCallable }) {
    const [feedback, setFeedback] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [pendingPool, setPendingPool] = useState(null);
    // Form state
    const [tokenSymbol, setTokenSymbol] = useState('');
    const [tokenContract, setTokenContract] = useState('');
    const [totalDeposit, setTotalDeposit] = useState('');
    const [durationDays, setDurationDays] = useState(30); // <<< THIS IS THE FIX
    const [decimals, setDecimals] = useState('');
    const [txId, setTxId] = useState('');
    const [isResettingDailyPoints, setIsResettingDailyPoints] = useState(false);
    const [resetDailyPointsFeedback, setResetDailyPointsFeedback] = useState('');

    useEffect(() => {
        if (session?.actor) {
            const poolQuery = query(collection(db, "partner_pools"), where("partnerId", "==", session.actor.toString()), where("status", "in", ["pending_deposit", "pending_approval"]));
            const unsubscribe = onSnapshot(poolQuery, (snapshot) => {
                if (!snapshot.empty) {
                    const poolData = { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
                    setPendingPool(poolData);
                } else {
                    setPendingPool(null);
                }
            });
            return () => unsubscribe();
        }
    }, [session]);

    const handleInitiate = async () => {
        if (!tokenSymbol || !tokenContract || !totalDeposit || !durationDays || !decimals) {
            setFeedback("Error: All fields are required.");
            return;
        }
        setIsLoading(true);
        setFeedback('Initiating pool...');
        try {
            const result = await initiatePartnerPoolCallable({
                tokenSymbol: tokenSymbol.toUpperCase(),
                tokenContract: tokenContract.toLowerCase(),
                totalDeposit: Number(totalDeposit),
                durationDays: Number(durationDays),
                decimals: Number(decimals)
            });
            setFeedback(`Pool initiated! Please send ${result.data.expectedAmount} ${tokenSymbol} to 'whatifreward' with memo: ${result.data.memo}`);
        } catch (error) {
            console.error("Full error from callable function:", error);
            let detailedMessage = `Error: ${error.message}`;
            if (error.details && error.details.errorStack) {
                detailedMessage += ` | SERVER STACK: ${error.details.errorStack}`;
            }
            setFeedback(detailedMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleVerify = async () => {
        if (!txId || !pendingPool) {
            setFeedback("Error: Transaction ID is required.");
            return;
        }
        setIsLoading(true);
        setFeedback('Verifying deposit...');
        try {
            const result = await verifyPartnerDepositCallable({ poolId: pendingPool.id, txId: txId });
            setFeedback(result.data.message);
        } catch (error) {
            setFeedback(`Error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div style={{ backgroundColor: 'rgba(0,0,0,0.7)', padding: '20px', borderRadius: '8px', maxWidth: '800px', margin: 'auto', textAlign: 'left' }}>
            <h2>Partner Portal</h2>
            {feedback && <p style={{ color: feedback.startsWith('Error') ? 'red' : 'green' }}>{feedback}</p>}

            {pendingPool?.status === 'pending_approval' && (
                <div>
                    <h3>Pool Awaiting Approval</h3>
                    <p>Your deposit has been verified! Your pool for <b>{pendingPool.totalDeposit} {pendingPool.tokenSymbol}</b> is now awaiting final review and approval from the admin.
                        Once approved, it will become active.</p>
                </div>
            )}

            {pendingPool?.status === 'pending_deposit' && (
                <div>
                    <h3>Step 2: Verify Your Deposit</h3>
                    <p style={{ color: 'yellow', fontWeight: 'bold' }}>
                        ACTION REQUIRED: Please send exactly <b style={{ color: 'cyan' }}>{pendingPool.totalDeposit.toFixed(pendingPool.decimals)} {pendingPool.tokenSymbol}</b> to account "<b style={{ color: 'cyan' }}>whatifreward</b>"
                        with the following **EXACT MEMO**:
                    </p>
                    <p style={{ backgroundColor: '#333', padding: '10px', borderRadius: '5px', wordBreak: 'break-all' }}>
                        <code style={{ color: '#0f0' }}>{pendingPool.depositMemo}</code>
                    </p>
                    <p>Once your transaction is complete, paste the Transaction ID (TxID) below and click verify.</p>
                    <input type="text" value={txId} onChange={(e) => setTxId(e.target.value)} placeholder="Enter Transaction ID (TxID)" className="modern-input" />
                    <button onClick={handleVerify} disabled={isLoading} className="modern-button" style={{ backgroundColor: '#007bff', marginTop: '15px' }}>
                        {isLoading ? 'Verifying...' : 'Verify Deposit'}
                    </button>
                </div>
            )}

            {!pendingPool && (
                <div>
                    <h3>Step 1: Create a New Reward Pool</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <input type="text" value={tokenSymbol} onChange={(e) => setTokenSymbol(e.target.value)} placeholder="Token Symbol (e.g., MYTOKEN)" className="modern-input" />
                        <input type="text" value={tokenContract} onChange={(e) => setTokenContract(e.target.value)} placeholder="Token Contract Account" className="modern-input" />
                        <input type="number" value={decimals} onChange={(e) => setDecimals(e.target.value)} placeholder="Token Decimals (e.g., 4)" className="modern-input" />
                        <input type="number" value={totalDeposit} onChange={(e) => setTotalDeposit(e.target.value)} placeholder="Total Tokens to Deposit" className="modern-input" />
                        <input type="number" value={durationDays} onChange={(e) => setDurationDays(e.target.value)} placeholder="Distribution Duration in Days (e.g., 30)" className="modern-input" />
                        <button onClick={handleInitiate} disabled={isLoading} className="modern-button" style={{ backgroundColor: '#28a745' }}>
                            {isLoading ? 'Initiating...' : 'Initiate Pool'}
                        </button>
                    </div>
                </div>
            )}
        </div>

    );
}

export default PartnerPortal;
